<template>
  <div class="pr-4">
    <!-- Loading -->
    <div v-if="loading" role="status" class="text-muted italic py-8">
      {{ $t("loading") }}
    </div>

    <!-- Empty: nothing tracked yet -->
    <div
      v-else-if="series.length === 0"
      class="flex flex-col items-start gap-4 py-12"
    >
      <p class="text-muted">{{ $t("dashboard.empty") }}</p>
      <router-link
        to="/explore"
        class="text-highlight uppercase tracking-widest text-sm hover:underline"
      >
        {{ $t("nav.explore") }}
      </router-link>
    </div>

    <template v-else>
      <!-- Your Upcoming -->
      <section>
        <h1 class="text-2xl uppercase font-semibold text-white">
          {{ $t("dashboard.yourUpcoming") }}
        </h1>

        <div class="flex flex-col lg:flex-row mt-6 gap-8 lg:gap-0">
          <!-- This week -->
          <div class="flex-1 min-w-0">
            <h2 class="text-subtle uppercase text-xs tracking-widest mb-4">
              {{ $t("dashboard.thisWeek") }}
            </h2>
            <!-- The empty backdrop boxes alone read as "still loading", so an
                 empty column says so in words. -->
            <p v-if="!week.length" class="text-muted text-sm mb-3">
              {{ $t("dashboard.noneThisWeek") }}
            </p>
            <div class="flex gap-6 overflow-x-auto pb-2">
              <UpcomingCard v-for="s in week" :key="s.id" :series="s" />
              <EmptyUpcomingCard v-for="n in weekEmpty" :key="`w-${n}`" />
            </div>
          </div>

          <!-- Divider -->
          <div
            class="hidden lg:block border-l border-primary-400 mx-8 self-stretch"
          ></div>

          <!-- This month -->
          <div class="flex-1 min-w-0">
            <h2 class="text-subtle uppercase text-xs tracking-widest mb-4">
              {{ $t("dashboard.thisMonth") }}
            </h2>
            <p v-if="!month.length" class="text-muted text-sm mb-3">
              {{ $t("dashboard.noneThisMonth") }}
            </p>
            <div class="flex gap-6 overflow-x-auto pb-2">
              <UpcomingCard v-for="s in month" :key="s.id" :series="s" />
              <EmptyUpcomingCard v-for="n in monthEmpty" :key="`m-${n}`" />
            </div>
          </div>
        </div>
      </section>

      <!-- Popular Upcoming -->
      <section class="mt-16">
        <h2 class="text-2xl uppercase font-semibold text-white">
          {{ $t("dashboard.popularUpcoming") }}
        </h2>
        <p class="text-subtle uppercase text-xs tracking-widest mt-2 mb-4">
          {{ $t("dashboard.thisWeek") }}
        </p>
        <div v-if="popular.length" class="flex gap-6 overflow-x-auto pb-2">
          <PopularUpcomingCard v-for="s in popular" :key="s.id" :series="s" />
        </div>
        <p v-else class="text-muted text-sm">
          {{ $t("dashboard.noneUpcoming") }}
        </p>
      </section>
    </template>
  </div>
</template>

<script>
import { daysUntilAir } from "@/utils/series";
import UpcomingCard from "@/components/series/UpcomingCard.vue";
import PopularUpcomingCard from "@/components/series/PopularUpcomingCard.vue";
import EmptyUpcomingCard from "@/components/series/EmptyUpcomingCard.vue";

// Minimum slots per column; unfilled slots render as empty backdrop boxes.
const MIN_SLOTS = 3;

function inRange(series, min, max) {
  const d = daysUntilAir(series);
  return d !== null && d >= min && d <= max;
}

export default {
  name: "Dashboard",
  components: { UpcomingCard, PopularUpcomingCard, EmptyUpcomingCard },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    series() {
      return Object.values(this.$store.getters.seriesList);
    },

    /** Airing within the next 7 days. */
    week() {
      return this.series.filter((s) => inRange(s, 0, 7));
    },

    /** Airing in roughly the rest of the month (8–31 days out). */
    month() {
      return this.series.filter((s) => inRange(s, 8, 31));
    },

    /** Empty backdrop boxes padding each column up to MIN_SLOTS. */
    weekEmpty() {
      return Math.max(MIN_SLOTS - this.week.length, 0);
    },
    monthEmpty() {
      return Math.max(MIN_SLOTS - this.month.length, 0);
    },

    /** Only series that are genuinely upcoming (airing today or later),
     *  soonest first. Ended / undated series are excluded. */
    popular() {
      return this.series
        .filter((s) => {
          const d = daysUntilAir(s);
          return d !== null && d >= 0;
        })
        .sort((a, b) => daysUntilAir(a) - daysUntilAir(b));
    },
  },

  async mounted() {
    // Only fetch if we don't already have data in the store.
    if (this.series.length === 0) {
      this.loading = true;
      try {
        await this.$store.dispatch("fetchAll");
      } catch {
        // Interceptor handles 401; nothing else to surface on the dashboard.
      } finally {
        this.loading = false;
      }
    }
  },
};
</script>
